import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { BRAND_LOGO_URL, formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/feed" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/feed", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center dp-grain px-4 py-12 overflow-hidden"
      data-testid="login-page"
    >
      <div className="dp-noise absolute inset-0 pointer-events-none" />

      {/* Decorative gradient blobs */}
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-[#6D28D9]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-[#2563EB]/20 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md dp-fade-in">
        <div className="flex flex-col items-center mb-8">
          <img
            src={BRAND_LOGO_URL}
            alt="Design Plus"
            className="h-20 w-20 rounded-2xl object-cover shadow-lg shadow-[#6D28D9]/20 ring-1 ring-black/5"
          />
          <h1 className="mt-5 text-3xl sm:text-4xl font-display font-extrabold tracking-tight">
            Welcome to{" "}
            <span className="dp-gradient-text">Design Plus</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground text-center">
            Sign in to access your team's apps portal
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@dplus.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-11 dp-gradient-bg text-white hover:opacity-90 transition-opacity font-medium shadow-md shadow-[#6D28D9]/20"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in <ArrowRight className="ml-1.5 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Internal portal · Design Plus team only
        </p>
      </div>
    </div>
  );
}
